import { useTranslation } from "react-i18next";
import { KeyRound } from "lucide-react";
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { MicrosoftIcon } from "@/components/icons/MicrosoftIcon";

interface AuthProviderBadgeProps {
  provider: string | null;
  /** "cell" — plain icon + label for table rows. "pill" — tooltipped rounded pill for drawer headers. */
  variant?: "cell" | "pill";
}

interface ProviderStyle {
  Icon: typeof GoogleIcon;
  labelKey: string;
  tooltipKey: string;
  tooltipDefault: string;
  pillClassName: string;
}

/** Keyed by Firebase's `sign_in_provider`. Anything else, `password` included, shows as a password user. */
const SOCIAL_PROVIDERS: Record<string, ProviderStyle> = {
  "google.com": {
    Icon: GoogleIcon,
    labelKey: "iam.authProviderBadge.google",
    tooltipKey: "iam.personDrawer.authProvider.google",
    tooltipDefault: "Signs in with Google",
    pillClassName: "bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400",
  },
  "microsoft.com": {
    Icon: MicrosoftIcon,
    labelKey: "iam.authProviderBadge.microsoft",
    tooltipKey: "iam.personDrawer.authProvider.microsoft",
    tooltipDefault: "Signs in with Microsoft",
    pillClassName: "border border-border bg-muted text-foreground",
  },
};

/**
 * Renders which auth provider a user signs in with. Single source for what
 * used to be a `AuthProviderCell` copy in the People table and an
 * `AuthProviderBadge` copy in the Person drawer.
 */
export function AuthProviderBadge({ provider, variant = "pill" }: AuthProviderBadgeProps) {
  const { t } = useTranslation();

  if (!provider) {
    return variant === "cell" ? <span className="text-xs text-muted-foreground">—</span> : null;
  }

  const social = SOCIAL_PROVIDERS[provider];

  if (variant === "cell") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
        {social ? (
          <social.Icon className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <KeyRound className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        {social ? t(social.labelKey) : t("iam.authProviderBadge.password")}
      </span>
    );
  }

  const badge = social ? (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium", social.pillClassName)}>
      <social.Icon className="h-2.5 w-2.5 shrink-0" />
      {t(social.labelKey)}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
      <KeyRound className="h-2.5 w-2.5 shrink-0" />
      {t("iam.authProviderBadge.password")}
    </span>
  );

  const tooltip = social
    ? t(social.tooltipKey, social.tooltipDefault)
    : t("iam.personDrawer.authProvider.password", "Signs in with email and password");

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="inline-flex shrink-0">
            {badge}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
